/**
 * Helpers for product details that are keyed by user-entered facility names (AM_VISION, iFIT,
 * FACILITY360) rather than by fixed field names.
 *
 * AM_VISION is keyed by facility name, but humps mangled those keys on the API round-trip
 * ("Gym A" was stored as "gym _a" and reloaded as "gymA") because AM_VISION — unlike iFIT and
 * FACILITY360 — was not exempted from camelize/decamelize in api/orderKeyConversion.ts. That
 * exemption now exists (DO-295), but orders saved before it still hold mangled keys, so reads
 * must keep matching on a form that survives the mangling. humps only ever eats separators and
 * changes case, so a lowercase alphanumeric-only key is stable across it.
 */
export const normalizeFacilityKey = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Finds the key holding `facilityName`'s entry. An exact hit wins so an order saved with intact
 * keys is never matched to a differently-spelled legacy twin ("Gym A" vs "GymA").
 */
export const findFacilityKey = (keys: string[], facilityName: string): string | undefined => {
  if (!facilityName) {
    return undefined;
  }
  if (keys.includes(facilityName)) {
    return facilityName;
  }
  const target = normalizeFacilityKey(facilityName);
  return keys.find(key => normalizeFacilityKey(key) === target);
};

/**
 * Re-keys a facility-keyed product-detail object onto the order's current facility names.
 *
 * Facilities with no stored entry get an empty object. Keys matching no current facility are
 * dropped — that covers renamed/removed facilities and the `pricing` array finalizeAndSubmitOrder
 * stores alongside them. Each stored key is consumed at most once, so two facilities whose names
 * normalize identically resolve deterministically (first wins) instead of duplicating counts.
 */
export const reindexFacilityDetails = <T>(
  details: Record<string, T> | undefined,
  facilityNames: string[],
): Record<string, T | Record<string, never>> => {
  const unclaimed = Object.keys(details ?? {});
  return facilityNames.reduce<Record<string, T | Record<string, never>>>((memo, name) => {
    const key = findFacilityKey(unclaimed, name);
    if (key === undefined || !details) {
      memo[name] = {};
      return memo;
    }
    unclaimed.splice(unclaimed.indexOf(key), 1);
    memo[name] = details[key];
    return memo;
  }, {});
};
